import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["className", "wrapperClassName", "innerClassName", "chat", "disabled", "error", "errorText", "full", "hint", "noBorders", "check", "darkMode", "inputClassName", "autoComplete", "autoFocus", "defaultValue", "endAdornment", "adornmentClassNames", "id", "inputProps", "label", "description", "maxLength", "name", "onChange", "onChangeValidation", "onFocus", "onBlur", "onKeyDown", "startAdornment", "type", "value", "data-testid", "data-cy", "textType", "responsive", "wrapperStyles", "transparencyMode", "htmlProps", "newSidebarEnabled"];
import { forwardRef, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import styled from 'styled-components';
import { baseMain, white } from "../styles/colors";
import transition from "../styles/transition";
import CLASSNAMES_BY_TEXT_TYPE from "../Text/responsiveClassnames";
import Adornment from "./Adornment";
import { ALTERNATIVE_BORDER_COLOR_CLASSNAMES, BACKGROUND_COLOR_CLASSNAMES, BASE_INPUT, BORDER_COLOR_CLASSNAMES, HINT_HEX_COLOR, INPUT_TEXT_COLOR_CLASSNAMES } from "./classnames";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var StyledInput = /*#__PURE__*/styled.input.withConfig({
  componentId: "sc-7y9917-0"
})(["\n  ", "\n"], function (_ref) {
  var hintColor = _ref.hintColor,
    darkMode = _ref.darkMode;
  return "\n    &::-webkit-input-placeholder {\n      color: ".concat(hintColor, ";\n    }\n    &:-moz-placeholder { /* Firefox 18- */\n      color: ").concat(hintColor, ";\n    }\n    &::-moz-placeholder {  /* Firefox 19+ */\n      color: ").concat(hintColor, ";\n    }\n    &:-ms-input-placeholder {\n      color: ").concat(hintColor, ";\n    }\n    &::placeholder {\n      color: ").concat(hintColor, ";\n    }\n    &:-webkit-autofill,\n    &:-webkit-autofill:hover,\n    &:-webkit-autofill:focus,\n    &:-webkit-autofill:active {\n      transition: background-color 5000s ease-in-out 0s;\n      -webkit-text-fill-color: ").concat(darkMode ? white : baseMain, " !important;\n    }\n  ");
});

/**
 * TextField component
 */
var TextField = function TextField(_ref2, ref) {
  var _ref2$className = _ref2.className,
    className = _ref2$className === void 0 ? '' : _ref2$className,
    _ref2$wrapperClassNam = _ref2.wrapperClassName,
    wrapperClassName = _ref2$wrapperClassNam === void 0 ? '' : _ref2$wrapperClassNam,
    _ref2$innerClassName = _ref2.innerClassName,
    innerClassName = _ref2$innerClassName === void 0 ? '' : _ref2$innerClassName,
    chat = _ref2.chat,
    disabled = _ref2.disabled,
    error = _ref2.error,
    errorText = _ref2.errorText,
    full = _ref2.full,
    hint = _ref2.hint,
    noBorders = _ref2.noBorders,
    check = _ref2.check,
    darkMode = _ref2.darkMode,
    inputClassName = _ref2.inputClassName,
    autoComplete = _ref2.autoComplete,
    autoFocus = _ref2.autoFocus,
    defaultValue = _ref2.defaultValue,
    endAdornment = _ref2.endAdornment,
    adornmentClassNames = _ref2.adornmentClassNames,
    id = _ref2.id,
    inputProps = _ref2.inputProps,
    label = _ref2.label,
    description = _ref2.description,
    maxLength = _ref2.maxLength,
    name = _ref2.name,
    onChangeProp = _ref2.onChange,
    onChangeValidation = _ref2.onChangeValidation,
    onFocusProp = _ref2.onFocus,
    onBlurProp = _ref2.onBlur,
    onKeyDown = _ref2.onKeyDown,
    startAdornment = _ref2.startAdornment,
    type = _ref2.type,
    value = _ref2.value,
    dataTestId = _ref2['data-testid'],
    dataCy = _ref2['data-cy'],
    _ref2$textType = _ref2.textType,
    textType = _ref2$textType === void 0 ? 'body3' : _ref2$textType,
    _ref2$responsive = _ref2.responsive,
    responsive = _ref2$responsive === void 0 ? 'sm' : _ref2$responsive,
    wrapperStyles = _ref2.wrapperStyles,
    transparencyMode = _ref2.transparencyMode,
    htmlProps = _ref2.htmlProps,
    _ref2$newSidebarEnabl = _ref2.newSidebarEnabled,
    newSidebarEnabled = _ref2$newSidebarEnabl === void 0 ? false : _ref2$newSidebarEnabl,
    textFieldProps = _objectWithoutProperties(_ref2, _excluded);
  var inputElement = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    focused = _useState2[0],
    setFocused = _useState2[1];
  useEffect(function () {
    if (autoFocus && inputElement.current) {
      inputElement.current.focus();
    }
  }, [autoFocus]);
  var onChange = function onChange(e) {
    if (typeof onChangeProp === 'function' && (typeof onChangeValidation !== 'function' || onChangeValidation(e.target.value))) {
      onChangeProp(e);
    }
  };
  var onFocus = function onFocus(e) {
    setFocused(true);
    if (typeof onFocusProp === 'function') {
      onFocusProp(e);
    }
  };
  var onBlur = function onBlur(e) {
    setFocused(false);
    if (typeof onBlurProp === 'function') {
      onBlurProp(e);
    }
  };
  var assignRef = function assignRef(i) {
    if (typeof ref === 'function') {
      ref(i);
    } else if (ref) {
      ref.current = i;
    }
    inputElement.current = i;
  };
  var fontClassNames = CLASSNAMES_BY_TEXT_TYPE(responsive)[textType];
  var backgroundState = darkMode ? 'dark' : 'light';
  var borderState = darkMode ? 'dark' : 'light';
  var inputState = darkMode ? 'dark' : 'light';
  if (transparencyMode) {
    backgroundState = 'transparency';
    borderState = 'transparency';
    inputState = 'transparency';
    if (focused && !chat) {
      backgroundState = 'transparency-focused';
      borderState = 'transparency-focused';
    }
    if (error || errorText) {
      inputState = 'transparency-error';
      borderState = 'error';
    }
    if (disabled) {
      backgroundState = 'transparency-disabled';
      inputState = 'transparency-disabled';
      borderState = 'disabled';
    }
  } else {
    if (focused && !chat) {
      borderState = darkMode ? 'dark-focused' : 'light-focused';
    }
    if (error || errorText) {
      inputState = darkMode ? 'dark-error' : 'light-error';
      borderState = 'error';
    }
    if (disabled) {
      backgroundState = darkMode ? 'dark-disabled' : 'light-disabled';
      inputState = darkMode ? 'dark-disabled' : 'light-disabled';
      borderState = 'disabled';
    }
  }
  var inputPaddingClassNames = '';
  if (!noBorders) {
    inputPaddingClassNames = chat ? 'py-8' : 'py-16';
    if (startAdornment || check || endAdornment) {
      inputPaddingClassNames = cn(inputPaddingClassNames, startAdornment ? 'pl-8' : 'pl-16', endAdornment || check ? 'pr-8' : 'pr-16');
    } else {
      inputPaddingClassNames = cn(inputPaddingClassNames, 'px-16');
    }
  }
  var adornmentProps = {
    startAdornment: startAdornment,
    endAdornment: endAdornment,
    adornmentClassNames: adornmentClassNames,
    chat: chat
  };
  return /*#__PURE__*/_jsxs("label", _objectSpread(_objectSpread(_objectSpread({
    className: cn('inline-flex flex-col', {
      'w-full': full
    }, className)
  }, textFieldProps), htmlProps), {}, {
    children: [label && /*#__PURE__*/_jsx("span", {
      className: cn('font-subtitle3 mb-8', darkMode || transparencyMode ? 'text-white' : 'text-base-main'),
      children: label
    }), description && /*#__PURE__*/_jsx("p", {
      className: cn('font-body4 mb-8', darkMode || transparencyMode ? 'text-base-lighter' : 'text-gray-dark'),
      children: description
    }), /*#__PURE__*/_jsx("div", {
      className: cn('inline-flex', wrapperClassName, newSidebarEnabled ? 'tw-rounded-0.8' : 'rounded-5', _defineProperty({}, "border border-solid ".concat(newSidebarEnabled ? ALTERNATIVE_BORDER_COLOR_CLASSNAMES[borderState] : BORDER_COLOR_CLASSNAMES[borderState], " ").concat(BACKGROUND_COLOR_CLASSNAMES[backgroundState]), !noBorders)),
      style: _objectSpread({
        transition: transition(['background-color', 'border'])
      }, wrapperStyles),
      children: /*#__PURE__*/_jsxs("div", {
        className: cn('flex w-full items-end', innerClassName),
        children: [startAdornment && /*#__PURE__*/_jsx(Adornment, _objectSpread({
          type: "start"
        }, adornmentProps)), /*#__PURE__*/_jsx(StyledInput, _objectSpread({
          "data-testid": dataTestId,
          "data-cy": dataCy,
          className: cn('crehana-ui-input', BASE_INPUT, INPUT_TEXT_COLOR_CLASSNAMES[inputState], fontClassNames, inputPaddingClassNames, inputClassName),
          hintColor: HINT_HEX_COLOR[backgroundState],
          darkMode: darkMode || transparencyMode,
          placeholder: hint,
          ref: assignRef,
          autoComplete: autoComplete,
          defaultValue: defaultValue ? defaultValue.toString() : undefined,
          disabled: disabled,
          id: id,
          name: name,
          type: type,
          value: value,
          onKeyDown: onKeyDown,
          maxLength: maxLength || undefined,
          onChange: onChange,
          onFocus: onFocus,
          onBlur: onBlur
        }, inputProps)), check && /*#__PURE__*/_jsx(Adornment, _objectSpread({
          type: "check"
        }, adornmentProps)), endAdornment && /*#__PURE__*/_jsx(Adornment, _objectSpread({
          type: "end"
        }, adornmentProps))]
      })
    }), errorText && /*#__PURE__*/_jsx("span", {
      className: cn('font-caption mt-8', disabled ? 'text-gray-dark' : 'text-red-main'),
      role: "alert",
      children: errorText
    })]
  }));
};
export default /*#__PURE__*/forwardRef(TextField);